// Crash Logger - persiste attraverso i reload
export class CrashLogger {
  private static instance: CrashLogger;
  private logKey = 'crash_logs';
  private logRateLimit = new Map<string, number>(); // Rate limiting per tipo di log
  private maxLogsPerMinute = 10; // Max 10 logs per minuto per tipo
  
  static getInstance(): CrashLogger {
    if (!CrashLogger.instance) {
      CrashLogger.instance = new CrashLogger();
    }
    return CrashLogger.instance;
  }

  private constructor() {
    this.initializeErrorHandlers();
    this.logEvent('LOGGER_INITIALIZED', { timestamp: new Date().toISOString() });
  }

  private initializeErrorHandlers() {
    // Cattura errori JavaScript
    window.addEventListener('error', (event) => {
      this.logEvent('JS_ERROR', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.stack,
        timestamp: new Date().toISOString()
      });
    });

    // Cattura errori di Promise
    window.addEventListener('unhandledrejection', (event) => {
      this.logEvent('UNHANDLED_PROMISE_REJECTION', {
        reason: event.reason?.toString(),
        stack: event.reason?.stack,
        timestamp: new Date().toISOString()
      });
    });

    // Cattura beforeunload per vedere se è un reload
    window.addEventListener('beforeunload', (event) => {
      this.logEvent('BEFORE_UNLOAD', {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      });
    });

    // Intercetta fetch per logging (con filtri per ridurre noise)
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const [input, init] = args;
      const url = typeof input === 'string' ? input : input.toString();
      
      // Filtra richieste che non vogliamo loggare per ridurre noise
      if (this.shouldSkipFetchLogging(url)) {
        return originalFetch(...args);
      }
      
      this.logEvent('FETCH_START', {
        url,
        method: init?.method || 'GET',
        timestamp: new Date().toISOString()
      });

      try {
        const response = await originalFetch(...args);
        
        // Solo logga risposte con errori o non successful
        if (!response.ok) {
          this.logEvent('FETCH_RESPONSE', {
            url,
            status: response.status,
            ok: response.ok,
            timestamp: new Date().toISOString()
          });
        }

        return response;
      } catch (error) {
        this.logEvent('FETCH_ERROR', {
          url,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString()
        });
        throw error;
      }
    };
  }

  logEvent(type: string, data: any) {
    try {
      // Rate limiting: controlla se abbiamo già loggato troppo questo tipo recentemente
      if (!this.shouldLog(type)) {
        return;
      }

      const logs = this.getLogs();
      const logEntry = {
        type,
        data,
        timestamp: new Date().toISOString(),
        url: window.location.href
      };
      
      logs.push(logEntry);
      
      // Mantieni solo gli ultimi 50 log
      if (logs.length > 50) {
        logs.splice(0, logs.length - 50);
      }
      
      sessionStorage.setItem(this.logKey, JSON.stringify(logs));
      
      // Solo logga in console per errori importanti, non per fetch routine
      if (this.isImportantLog(type)) {
        console.log(`[CrashLogger] ${type}:`, data);
      }
    } catch (error) {
      console.error('Failed to log event:', error);
    }
  }

  private shouldLog(type: string): boolean {
    const now = Date.now();
    const lastLogged = this.logRateLimit.get(type) || 0;
    
    // Rate limit: max X logs per minuto per tipo
    if (now - lastLogged < (60000 / this.maxLogsPerMinute)) {
      return false;
    }
    
    this.logRateLimit.set(type, now);
    return true;
  }

  private isImportantLog(type: string): boolean {
    // Solo logga in console per errori critici, non per fetch routine
    const importantTypes = [
      'JS_ERROR', 
      'UNHANDLED_PROMISE_REJECTION', 
      'FETCH_ERROR',
      'LOGGER_INITIALIZED'
    ];
    return importantTypes.includes(type);
  }

  private shouldSkipFetchLogging(url: string): boolean {
    // Filtra richieste che generano troppo noise nei logs
    const skipPatterns = [
      '/favicon.ico',
      '.css',
      '.js',
      '.map',
      '/hot-update',
      '/sockjs-node',
      '/__webpack_dev_server__',
      '/ping',
      '/health',
      '/heartbeat',
      '/metrics'
    ];
    
    return skipPatterns.some(pattern => url.includes(pattern));
  }

  getLogs(): any[] {
    try {
      const stored = sessionStorage.getItem(this.logKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  clearLogs() {
    sessionStorage.removeItem(this.logKey);
  }

  dumpLogs() {
    const logs = this.getLogs();
    console.group('🚨 CRASH LOGS');
    logs.forEach((log, index) => {
      console.log(`${index + 1}. [${log.type}] ${log.timestamp}:`, log.data);
    });
    console.groupEnd();
    return logs;
  }
}

export const crashLogger = CrashLogger.getInstance();